import React, { useState } from "react";
import Papa from "papaparse";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from "@mui/material";

interface VariablesFileUploadProps {
  setWorkingScript: React.Dispatch<React.SetStateAction<any>>;
  workingScript: any;
  activeTab: number;
  autoCorrelateByIndex: boolean;
  maxVariablesReached: boolean;
  showAddVariableTab: boolean;
}

const VariablesFileUpload: React.FC<VariablesFileUploadProps> = ({
  setWorkingScript,
  workingScript,
  activeTab,
  autoCorrelateByIndex,
  maxVariablesReached,
  showAddVariableTab,
}) => {
  const [previewData, setPreviewData] = useState<string[][]>([]);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [showError, setShowError] = useState<boolean>(false);

  const MAX_VARIABLES = 2;

  const handleVariablesFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const parsedData = await new Promise<string[][]>((resolve, reject) => {
        Papa.parse(file, {
          complete: (result: any) => resolve(result.data),
          error: (error) => reject(error),
          header: false,
        });
      });

      if (parsedData.length === 0) return;

      const header = parsedData[0].map((h) => (h ?? "").toString().trim());
      const rows = parsedData
        .slice(1)
        .map((r: any[]) => r.map((c) => (c ?? "").toString()));

      if (header.length > MAX_VARIABLES) {
        setShowError(true);
      } else {
        setShowError(false);
      }

      const allowedHeaders = header.slice(0, MAX_VARIABLES);
      const allowedRows = rows.map((r) => r.slice(0, MAX_VARIABLES));

      setPreviewData([allowedHeaders, ...allowedRows]);
      setCsvFile(file);

      if (event.target) event.target.value = "";
    } catch (error) {
      console.error("Error processing CSV:", error);
    }
  };

  const handleApplyChanges = () => {
    if (!previewData.length) return;

    const [headers, ...rows] = previewData;
    const allowedHeaders = headers.slice(0, MAX_VARIABLES);

    setWorkingScript((prev: any) => {
      const newGroups =
        prev && Array.isArray(prev.variableGroups) && prev.variableGroups.length
          ? JSON.parse(JSON.stringify(prev.variableGroups))
          : [
              {
                name: "Default Group",
                variables: [],
              },
            ];

      const currentGroup = newGroups[0] as any;
      if (!currentGroup.variables || !Array.isArray(currentGroup.variables)) {
        currentGroup.variables = [];
      }

      const existingVarNames = currentGroup.variables.map((v: any) => v.name);

      allowedHeaders.forEach((variableName: string) => {
        if (!existingVarNames.includes(variableName)) {
          currentGroup.variables.push({
            name: variableName,
            values: [],
          });
        }
      });

      allowedHeaders.forEach((variableName: string, colIndex: number) => {
        const targetVar = currentGroup.variables.find(
          (v: any) => v.name === variableName
        );
        if (!targetVar) return;

        targetVar.values = rows
          .map((row) => (row[colIndex] ?? "").toString())
          .filter((v) => v && v.trim() !== "")
          .map((v) => ({ value: v.trim(), correlations: {} }));
      });

      if (autoCorrelateByIndex && currentGroup.variables.length > 1) {
        const [primaryHeader, ...otherHeaders] = allowedHeaders;
        const primaryVariable = currentGroup.variables.find(
          (v: any) => v.name === primaryHeader
        );

        rows.forEach((row, rowIndex) => {
          const primaryValue =
            primaryVariable?.values[rowIndex]?.value ??
            (row[0] ?? "").toString().trim();

          if (!primaryValue) return;

          const primaryEntry =
            primaryVariable?.values[rowIndex] ||
            (primaryVariable!.values[rowIndex] = {
              value: primaryValue,
              correlations: {},
            });

          otherHeaders.forEach((header, hIndex) => {
            const targetVar = currentGroup.variables.find(
              (v: any) => v.name === header
            );
            const correlatedValue = (row[hIndex + 1] ?? "").toString().trim();

            if (targetVar && correlatedValue) {
              primaryEntry.correlations[header] = correlatedValue;
            }
          });
        });
      }

      const result = { ...(prev || {}), variableGroups: newGroups };
      return result;
    });

    setPreviewData([]);
    setCsvFile(null);
    setShowError(false);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        variant="outlined"
        component="label"
        disabled={maxVariablesReached}>
        Upload variables and values from CSV
        <input
          type="file"
          hidden
          accept=".csv"
          onChange={handleVariablesFileUpload}
        />
      </Button>

      {showError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          The CSV contains more than 2 columns. Only the first two were
          considered.
        </Alert>
      )}

      {previewData.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Preview:
          </Typography>

          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              borderRadius: 2,
              border: "1px solid #ccc",
            }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {previewData[0].map((header, i) => (
                    <TableCell key={i}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.slice(1).map((row, rIndex) => (
                  <TableRow key={rIndex}>
                    {row.map((cell, cIndex) => (
                      <TableCell key={cIndex}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyChanges}>
              Apply Changes
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VariablesFileUpload;
